import React, { useEffect, useMemo, useState } from "react";
import { FieldValues, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { Image, Scissors, X } from "react-feather";
import toast from "react-hot-toast";
import {
  cleanupPanorama,
  generatePanorama,
  getPanoramaInfo,
} from "../../api/panorama";
import {
  getPanoramaAspectRatioRange,
  getPanoramaItemLimit,
  getPanoramaMaxOutputEdge,
  getValidPanoramaSliceCounts,
  isPanoramaAspectRatioValid,
  isPanoramaMarginValid,
  MAX_PANORAMA_MARGIN_PERCENT,
  PanoramaAspectRatio,
  PanoramaInfoResponse,
  PanoramaRequirements,
} from "../../shared/panorama";
import { SelectedFile } from "../../types/PostSelection";
import { Feed, TypeMapping } from "../../types/api/Feed";
import { handleError } from "../../utils/handleError";
import { Portal } from "../Modal";
import { PanoramaPreview } from "./Preview";

const ASPECT_RATIO_PRESETS: {
  label: string;
  value: PanoramaAspectRatio;
}[] = [
  { label: "1:1", value: { width: 1, height: 1 } },
  { label: "4:5", value: { width: 4, height: 5 } },
  { label: "3:4", value: { width: 3, height: 4 } },
  { label: "16:9", value: { width: 16, height: 9 } },
  { label: "9:16", value: { width: 9, height: 16 } },
  { label: "1.91:1", value: { width: 1.91, height: 1 } },
];

export function PanoramaSlicer({
  feed,
  sourceFile,
  watch,
  setValue,
}: {
  feed: Feed;
  sourceFile?: SelectedFile;
  watch: UseFormWatch<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [includeSlices, setIncludeSlices] = useState(true);
  const [includeBackground, setIncludeBackground] = useState(false);
  const [sliceCount, setSliceCount] = useState(2);
  const [backgroundColor, setBackgroundColor] = useState<"black" | "white">(
    "white",
  );
  const [horizontalMargin, setHorizontalMargin] = useState(5);
  const [verticalMargin, setVerticalMargin] = useState(5);
  const [aspectRatio, setAspectRatio] = useState<PanoramaAspectRatio>({
    width: 1,
    height: 1,
  });
  const [backgroundPosition, setBackgroundPosition] = useState<
    "before" | "after"
  >("before");
  const [panoramaInfo, setPanoramaInfo] = useState<PanoramaInfoResponse | null>(
    null,
  );
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const requirements = feed.requirements as PanoramaRequirements | null;
  const range = getPanoramaAspectRatioRange(requirements);
  const itemLimit = getPanoramaItemLimit(requirements);
  const maxOutputEdge = getPanoramaMaxOutputEdge(requirements);
  const canUsePanorama =
    !!sourceFile &&
    sourceFile.mine_type.startsWith("image/") &&
    !!sourceFile.imageDerivatives?.originalPath &&
    !!range &&
    itemLimit !== null &&
    maxOutputEdge !== null;
  const effectiveRange = panoramaInfo?.aspectRatioRange ?? range;
  const effectiveItemLimit = panoramaInfo?.itemLimit ?? itemLimit;
  const effectiveMaxOutputEdge = panoramaInfo?.maxOutputEdge ?? maxOutputEdge;

  const validSliceCounts = useMemo(
    () =>
      panoramaInfo
        ? getValidPanoramaSliceCounts({
            sourceWidth: panoramaInfo.sourceWidth,
            sourceHeight: panoramaInfo.sourceHeight,
            requirements: {
              PHOTO: {
                min_aspect_ratio: panoramaInfo.aspectRatioRange.min,
                max_aspect_ratio: panoramaInfo.aspectRatioRange.max,
                max_items: panoramaInfo.itemLimit,
              },
              total_max_items: panoramaInfo.itemLimit,
            },
            includeBackground,
          })
        : [],
    [includeBackground, panoramaInfo],
  );

  const selectedFiles = watch(`${feed.slug}.selected_files`) as
    SelectedFile[] | null;

  const validPresets = useMemo(
    () =>
      effectiveRange
        ? ASPECT_RATIO_PRESETS.filter((preset) =>
            isPanoramaAspectRatioValid(preset.value, effectiveRange),
          )
        : [],
    [effectiveRange],
  );

  useEffect(() => {
    if (validSliceCounts.length > 0 && !validSliceCounts.includes(sliceCount)) {
      setSliceCount(validSliceCounts[0]);
    }
  }, [sliceCount, validSliceCounts]);

  if (
    !canUsePanorama ||
    !effectiveRange ||
    effectiveMaxOutputEdge === null ||
    !sourceFile
  ) {
    return null;
  }

  const openModal = async () => {
    setIsOpen(true);
    setIsLoadingInfo(true);

    try {
      const info = await getPanoramaInfo({
        originalPath: sourceFile.imageDerivatives!.originalPath,
        feedId: feed.id,
      });
      const infoRequirements: PanoramaRequirements = {
        PHOTO: {
          min_aspect_ratio: info.aspectRatioRange.min,
          max_aspect_ratio: info.aspectRatioRange.max,
          max_items: info.itemLimit,
        },
        total_max_items: info.itemLimit,
      };
      const counts = getValidPanoramaSliceCounts({
        sourceWidth: info.sourceWidth,
        sourceHeight: info.sourceHeight,
        requirements: infoRequirements,
        includeBackground: false,
      });
      const presets = ASPECT_RATIO_PRESETS.filter((preset) =>
        isPanoramaAspectRatioValid(preset.value, info.aspectRatioRange),
      );
      const defaultRatio = presets.find((preset) => preset.label === "1:1")
        ?.value ??
        presets[0]?.value ?? {
          width: info.aspectRatioRange.min,
          height: 1,
        };

      setPanoramaInfo(info);
      setIncludeSlices(counts.length > 0);
      setIncludeBackground(counts.length === 0);
      setSliceCount(counts[0] ?? 2);
      setBackgroundColor("white");
      setHorizontalMargin(5);
      setVerticalMargin(5);
      setAspectRatio(defaultRatio);
      setBackgroundPosition("before");
    } catch (error) {
      setIsOpen(false);
      handleError(error as Error);
    } finally {
      setIsLoadingInfo(false);
    }
  };

  const handleBackgroundToggle = (enabled: boolean) => {
    setIncludeBackground(enabled);
    if (!panoramaInfo) return;

    const counts = getValidPanoramaSliceCounts({
      sourceWidth: panoramaInfo.sourceWidth,
      sourceHeight: panoramaInfo.sourceHeight,
      requirements: {
        PHOTO: {
          min_aspect_ratio: panoramaInfo.aspectRatioRange.min,
          max_aspect_ratio: panoramaInfo.aspectRatioRange.max,
          max_items: panoramaInfo.itemLimit,
        },
        total_max_items: panoramaInfo.itemLimit,
      },
      includeBackground: enabled,
    });

    if (includeSlices && counts.length === 0) {
      setIncludeSlices(false);
    } else if (counts.length > 0 && !counts.includes(sliceCount)) {
      setSliceCount(counts[0]);
    }
  };

  const backgroundIsValid =
    !includeBackground ||
    (isPanoramaAspectRatioValid(aspectRatio, effectiveRange) &&
      isPanoramaMarginValid(horizontalMargin) &&
      isPanoramaMarginValid(verticalMargin));
  const slicesAreValid =
    !includeSlices || validSliceCounts.includes(sliceCount);
  const canGenerate =
    (includeSlices || includeBackground) &&
    backgroundIsValid &&
    slicesAreValid &&
    !!panoramaInfo &&
    !isGenerating;

  const handleGenerate = async () => {
    if (!canGenerate) return;

    const generationId = crypto.randomUUID();
    setIsGenerating(true);
    try {
      const outputCount =
        (includeSlices ? sliceCount : 0) + (includeBackground ? 1 : 0);
      const assets = [];

      for (let outputIndex = 0; outputIndex < outputCount; outputIndex++) {
        const result = await generatePanorama({
          generationId,
          outputIndex,
          originalPath: sourceFile.imageDerivatives!.originalPath,
          feedId: feed.id,
          slices: includeSlices ? { count: sliceCount } : undefined,
          background: includeBackground
            ? {
                color: backgroundColor,
                horizontalMarginPercent: horizontalMargin,
                verticalMarginPercent: verticalMargin,
                aspectRatio,
                position: backgroundPosition,
              }
            : undefined,
        });
        assets.push(...result.assets);
      }

      const generatedAt = Date.now();
      const generatedFiles: SelectedFile[] = assets.map((asset, index) => ({
        id: -(generatedAt + index),
        _id: asset.storageId,
        name: asset.name,
        url: asset.thumbnailUrl,
        mine_type: asset.mimeType,
        altText: sourceFile.altText || "",
        imageDerivatives: asset.imageDerivatives,
      }));

      setValue(`${feed.slug}.selected_files`, generatedFiles, {
        shouldDirty: true,
      });

      const typeMapping = feed.type_mapping as TypeMapping | null;
      const defaultType = watch("default.type") as string | undefined;
      const nextType =
        generatedFiles.length > 1
          ? (typeMapping?.CAROUSEL?.type ?? "CAROUSEL")
          : (typeMapping?.[defaultType ?? "PHOTO"]?.type ??
            defaultType ??
            "PHOTO");
      setValue(`${feed.slug}.type`, nextType, { shouldDirty: true });

      toast.success(
        `Generated ${generatedFiles.length} panorama image${
          generatedFiles.length === 1 ? "" : "s"
        } for ${feed.name}`,
      );
      setIsOpen(false);
    } catch (error) {
      try {
        await cleanupPanorama(generationId);
      } catch (cleanupError) {
        handleError(cleanupError as Error);
      }
      handleError(error as Error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="my-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => void openModal()}
        >
          <Scissors size={16} />
          {selectedFiles?.some((file) => file.id && file.id < 0)
            ? "Regenerate Panorama"
            : "Slice Panorama"}
        </button>
        {selectedFiles && selectedFiles.length > 0 && (
          <span className="badge badge-outline">
            {selectedFiles.length} platform image
            {selectedFiles.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {selectedFiles && selectedFiles.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {selectedFiles.map((file) => (
            <img
              key={file.imageDerivatives?.originalPath ?? file.name}
              src={file.url}
              alt={file.altText || ""}
              className="h-16 w-16 rounded-lg object-cover"
            />
          ))}
        </div>
      )}

      {isOpen && (
        <Portal>
          <div className="modal modal-open">
            <div className="modal-box max-w-3xl bg-base-200">
              <button
                type="button"
                className="btn btn-sm btn-circle btn-ghost absolute right-3 top-3"
                onClick={() => setIsOpen(false)}
                disabled={isGenerating}
              >
                <X size={16} />
              </button>

              <div className="prose mb-6">
                <h2>Panorama for {feed.name}</h2>
                <p>
                  Accepted ratio: {effectiveRange.min.toFixed(3)} to{" "}
                  {effectiveRange.max.toFixed(3)} (width / height). Maximum{" "}
                  {effectiveItemLimit} images. Output is limited to{" "}
                  {effectiveMaxOutputEdge.toLocaleString()} px on the longest
                  edge.
                </p>
              </div>

              {isLoadingInfo ? (
                <div className="flex min-h-48 items-center justify-center">
                  <span className="loading loading-spinner loading-lg" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="card bg-base-100">
                    <div className="card-body">
                      <label className="label cursor-pointer justify-start gap-3">
                        <input
                          type="checkbox"
                          className="toggle toggle-primary"
                          checked={includeSlices}
                          disabled={validSliceCounts.length === 0}
                          onChange={(event) =>
                            setIncludeSlices(event.target.checked)
                          }
                        />
                        <span className="label-text font-semibold">
                          Slice the panorama
                        </span>
                      </label>

                      {includeSlices && (
                        <label className="form-control">
                          <span className="label-text mb-2">
                            Number of slices
                          </span>
                          <select
                            className="select w-full"
                            value={sliceCount}
                            onChange={(event) =>
                              setSliceCount(Number(event.target.value))
                            }
                          >
                            {validSliceCounts.map((count) => (
                              <option key={count} value={count}>
                                {count} slices
                              </option>
                            ))}
                          </select>
                        </label>
                      )}

                      {validSliceCounts.length === 0 && (
                        <div className="alert alert-warning">
                          No slice count fits this platform ratio and item
                          limit.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="card bg-base-100">
                    <div className="card-body">
                      <label className="label cursor-pointer justify-start gap-3">
                        <input
                          type="checkbox"
                          className="toggle toggle-primary"
                          checked={includeBackground}
                          onChange={(event) =>
                            handleBackgroundToggle(event.target.checked)
                          }
                        />
                        <span className="label-text font-semibold">
                          Add a background image
                        </span>
                      </label>

                      {includeBackground && (
                        <div className="space-y-4">
                          <div className="join">
                            {(["black", "white"] as const).map((color) => (
                              <button
                                key={color}
                                type="button"
                                className={`btn join-item ${
                                  backgroundColor === color ? "btn-primary" : ""
                                }`}
                                onClick={() => setBackgroundColor(color)}
                              >
                                {color === "black" ? "Black" : "White"}
                              </button>
                            ))}
                          </div>

                          <div className="grid gap-4 md:grid-cols-2">
                            <MarginField
                              label="Horizontal margin on each side"
                              value={horizontalMargin}
                              onChange={setHorizontalMargin}
                            />
                            <MarginField
                              label="Vertical margin on each side"
                              value={verticalMargin}
                              onChange={setVerticalMargin}
                            />
                          </div>

                          <div>
                            <div className="label-text mb-2">
                              Output aspect ratio
                            </div>
                            <div className="mb-3 flex flex-wrap gap-2">
                              {validPresets.map((preset) => (
                                <button
                                  key={preset.label}
                                  type="button"
                                  className="btn btn-outline btn-xs"
                                  onClick={() => setAspectRatio(preset.value)}
                                >
                                  {preset.label}
                                </button>
                              ))}
                            </div>
                            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                className="input w-full"
                                value={aspectRatio.width}
                                onChange={(event) =>
                                  setAspectRatio((current) => ({
                                    ...current,
                                    width: Number(event.target.value),
                                  }))
                                }
                              />
                              <span>:</span>
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                className="input w-full"
                                value={aspectRatio.height}
                                onChange={(event) =>
                                  setAspectRatio((current) => ({
                                    ...current,
                                    height: Number(event.target.value),
                                  }))
                                }
                              />
                            </div>
                            {!isPanoramaAspectRatioValid(
                              aspectRatio,
                              effectiveRange,
                            ) && (
                              <p className="mt-2 text-sm text-error">
                                The output ratio must be inside this platform’s
                                accepted range.
                              </p>
                            )}
                          </div>

                          {includeSlices && (
                            <label className="form-control">
                              <span className="label-text mb-2">
                                Background image position
                              </span>
                              <select
                                className="select w-full"
                                value={backgroundPosition}
                                onChange={(event) =>
                                  setBackgroundPosition(
                                    event.target.value as "before" | "after",
                                  )
                                }
                              >
                                <option value="before">
                                  Before the slices
                                </option>
                                <option value="after">After the slices</option>
                              </select>
                            </label>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <PanoramaPreview
                    sourceUrl={sourceFile.url}
                    sourceAlt={sourceFile.altText || sourceFile.name}
                    sourceWidth={panoramaInfo!.sourceWidth}
                    sourceHeight={panoramaInfo!.sourceHeight}
                    includeSlices={includeSlices}
                    sliceCount={sliceCount}
                    includeBackground={includeBackground}
                    backgroundColor={backgroundColor}
                    horizontalMargin={horizontalMargin}
                    verticalMargin={verticalMargin}
                    aspectRatio={aspectRatio}
                    backgroundPosition={backgroundPosition}
                  />
                </div>
              )}

              <div className="modal-action">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setIsOpen(false)}
                  disabled={isGenerating || isLoadingInfo}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleGenerate}
                  disabled={!canGenerate || isLoadingInfo}
                >
                  {isGenerating ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    <Image size={16} />
                  )}
                  {isGenerating ? "Generating..." : "Generate Images"}
                </button>
              </div>
            </div>
            <button
              type="button"
              className="modal-backdrop"
              onClick={() => !isGenerating && setIsOpen(false)}
            >
              close
            </button>
          </div>
        </Portal>
      )}
    </div>
  );
}

function MarginField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="form-control">
      <span className="label-text mb-2">{label}</span>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min="0"
          max={MAX_PANORAMA_MARGIN_PERCENT}
          value={value}
          className="range range-primary"
          onChange={(event) => onChange(Number(event.target.value))}
        />
        <input
          type="number"
          min="0"
          max={MAX_PANORAMA_MARGIN_PERCENT}
          value={value}
          className="input input-sm w-20"
          onChange={(event) => onChange(Number(event.target.value))}
        />
        <span>%</span>
      </div>
    </label>
  );
}
